import 'bootstrap/dist/css/bootstrap.css';
import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import Spinner from '../../../shared/spinner';
import { FinanciamentoEmpregadorDTO } from '../../../shared/dto/financiamentoEmpregadorDTO';
import FinanciamentoEmpregadorService from '../../../services/financiamentoEmpregadorService';
import CustomAlert from '../../../shared/customAlert';
import { useVerificaPermissao } from '../../../shared/useVerificaPermissao';
import { ProtectedAction } from '../../../AccessControl';

function FinanciamentoEmpregador() {
  useVerificaPermissao();
  const location = useLocation();
  const { idFinanciamento, codigoFinanciamento, nomeFinanciamento } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);
  const [empregadoresDisponiveis, setEmpregadoresDisponiveis] = useState<FinanciamentoEmpregadorDTO[]>([]);
  const [empregadoresSelecionados, setEmpregadoresSelecionados] = useState<FinanciamentoEmpregadorDTO[]>([]);

  const financiamentoEmpregadorService = new FinanciamentoEmpregadorService();

  const listarEmpregadores = async () => {
    try {
      const response = await financiamentoEmpregadorService.listarFinanciamentoEmpregador(idFinanciamento);
      const selecionados = response.data.filter((e: any) => e.ativo);
      const disponiveis = response.data.filter((e: any) => !e.ativo);

      setEmpregadoresDisponiveis(disponiveis);
      setEmpregadoresSelecionados(selecionados);
    } catch (err) {
      console.error(err);
    }
  };

  const moverEmpregadores = (
    source: FinanciamentoEmpregadorDTO[],
    setSource: React.Dispatch<React.SetStateAction<FinanciamentoEmpregadorDTO[]>>,
    target: FinanciamentoEmpregadorDTO[],
    setTarget: React.Dispatch<React.SetStateAction<FinanciamentoEmpregadorDTO[]>>,
    ids: number[]
  ) => {
    const moving = source.filter((e) => ids.includes(Number(e.empregador)));
    const remaining = source.filter((e) => !ids.includes(Number(e.empregador)));
    setSource(remaining);
    setTarget([...target, ...moving]);
  };

  const moverTodos = (
    source: FinanciamentoEmpregadorDTO[],
    setSource: React.Dispatch<React.SetStateAction<FinanciamentoEmpregadorDTO[]>>,
    target: FinanciamentoEmpregadorDTO[],
    setTarget: React.Dispatch<React.SetStateAction<FinanciamentoEmpregadorDTO[]>>
  ) => {
    setTarget([...target, ...source]);
    setSource([]);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    try {
      const todos = [...empregadoresDisponiveis, ...empregadoresSelecionados].map((item) => ({
        ...item,
        ativo: empregadoresSelecionados.some((e) => Number(e.empregador) === Number(item.empregador))
      }));
      await financiamentoEmpregadorService.incluirAlterarFinanciamentoEmpregador(idFinanciamento, todos);
      setAlert({ message: 'Atualização realizada com sucesso!', type: 'success' });
    } catch (err) {
      setAlert({ message: 'Erro ao atualizar lista.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    listarEmpregadores();
  }, []);

  return (
    <div className="container mt-3">
      <div className="d-flex justify-content-between mb-3 align-items-center">
        <h5>Cadastro &gt; Promotora &gt; Empregadores</h5>
        <Button variant="secondary" onClick={() => window.history.back()}>Voltar</Button>
      </div>

      {alert && <CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />}
      {loading ? <Spinner loading={loading} /> : (
        <>
          {/* Informações do financiamento */}
          <div className="row mb-3">
            <div className="col-md-1">
              <label>ID</label>
              <div className="form-control">{idFinanciamento}</div>
            </div>
            <div className="col-md-2">
              <label>Código</label>
              <div className="form-control">{codigoFinanciamento}</div>
            </div>
            <div className="col-md-5">
              <label>Nome</label>
              <div className="form-control">{nomeFinanciamento}</div>
            </div>
          </div>

          {/* Formulário principal */}
          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* Lista de disponíveis */}
              <div className="col-md-5">
                <label>Empregadores Disponíveis</label>
                <select
                  multiple
                  className="form-control"
                  id="listaDisponiveis"
                  size={15}
                  style={{ maxWidth: '100%' }}
                  onChange={(e) => { /* apenas visual */ }}
                >
                  {empregadoresDisponiveis.map((e) => (
                    <option key={e.empregador} value={e.empregador}>
                      {e.empregador} - {e.nomeEmpregador}
                    </option>
                  ))}
                </select>
              </div>

              {/* Botões de mover */}
              <div className="col-md-2 d-flex flex-column justify-content-center align-items-center gap-2">
                <ProtectedAction>
                  <Button variant="secondary" type="button" style={{ width: '40px' }} onClick={() => {
                    const selected = Array.from((document.getElementById('listaDisponiveis') as HTMLSelectElement)?.selectedOptions || []);
                    moverEmpregadores(
                      empregadoresDisponiveis,
                      setEmpregadoresDisponiveis,
                      empregadoresSelecionados,
                      setEmpregadoresSelecionados,
                      selected.map((opt: any) => Number(opt.value))
                    );
                  }}>{'>'}</Button>

                  <Button variant="secondary" type="button" style={{ width: '40px' }} onClick={() => {
                    const selected = Array.from((document.getElementById('listaSelecionados') as HTMLSelectElement)?.selectedOptions || []);
                    moverEmpregadores(
                      empregadoresSelecionados,
                      setEmpregadoresSelecionados,
                      empregadoresDisponiveis,
                      setEmpregadoresDisponiveis,
                      selected.map((opt: any) => Number(opt.value))
                    );
                  }}>{'<'}</Button>

                  <Button variant="secondary" type="button" style={{ width: '40px' }} onClick={() => moverTodos(
                    empregadoresDisponiveis,
                    setEmpregadoresDisponiveis,
                    empregadoresSelecionados,
                    setEmpregadoresSelecionados
                  )}>{'>>'}</Button>

                  <Button variant="secondary" type="button" style={{ width: '40px' }} onClick={() => moverTodos(
                    empregadoresSelecionados,
                    setEmpregadoresSelecionados,
                    empregadoresDisponiveis,
                    setEmpregadoresDisponiveis
                  )}>{'<<'}</Button>
                </ProtectedAction>
              </div>

              {/* Lista de selecionados */}
              <div className="col-md-5">
                <label>Empregadores Selecionados</label>
                <select
                  multiple
                  className="form-control"
                  id="listaSelecionados"
                  size={15}
                  style={{ maxWidth: '100%' }}
                  onChange={(e) => { /* apenas visual */ }}
                >
                  {empregadoresSelecionados.map((e) => (
                    <option key={e.empregador} value={e.empregador}>
                      {e.empregador} - {e.nomeEmpregador}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Botão de ação */}
            <div className="mt-4">
              <ProtectedAction>
                <Button type="submit" variant="success">Atualizar Lista</Button>
              </ProtectedAction>  
            </div>
          </form>
        </>
      )}
    </div>
  );
}

export default FinanciamentoEmpregador;
